import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, API } from "@/lib/api";
import { useYear } from "@/context/YearContext";
import { formatINR, MONTHS } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Download, Upload, FileText, Trash2, Save, Sparkles, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Budget() {
  const { year } = useYear();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dirty, setDirty] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editExp, setEditExp] = useState(null);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ category_id: "", name: "", notes: "", reserve_treatment: "core", cost_behaviour: "fixed" });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [e, c, m] = await Promise.all([
      api.get("/expenses", { params: { year } }),
      api.get("/categories"),
      api.get("/budget/meta", { params: { year } }),
    ]);
    setExpenses(e.data);
    setCategories(c.data);
    setMeta(m.data);
    setDirty({});
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const setCell = (expId, monthIdx, value) => {
    setExpenses((prev) => prev.map((e) => {
      if (e.id !== expId) return e;
      const months = [...e.months];
      months[monthIdx] = value === "" ? 0 : Math.max(0, parseFloat(value) || 0);
      return { ...e, months };
    }));
    setDirty((d) => ({ ...d, [expId]: true }));
  };

  const saveRow = async (exp) => {
    try {
      await api.put(`/expenses/${exp.id}`, { months: exp.months, notes: exp.notes });
      setDirty((d) => { const n = { ...d }; delete n[exp.id]; return n; });
      toast.success(`Saved "${exp.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const addExpense = async () => {
    if (!form.category_id || !form.name.trim()) { toast.error("Select a category and enter an expense name"); return; }
    try {
      if (editExp) {
        await api.put(`/expenses/${editExp.id}`, form);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", { year, ...form, months: Array(12).fill(0) });
        toast.success("Expense added");
      }
      setForm({ category_id: "", name: "", notes: "", reserve_treatment: "core", cost_behaviour: "fixed" });
      setEditExp(null);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const openEdit = (exp) => {
    setEditExp(exp);
    setForm({ category_id: exp.category_id, name: exp.name, notes: exp.notes || "",
              reserve_treatment: exp.reserve_treatment || "core", cost_behaviour: exp.cost_behaviour || "fixed" });
    setDialogOpen(true);
  };

  const toggleClass = async (exp, field) => {
    if (locked) return;
    const next = field === "reserve_treatment"
      ? (exp.reserve_treatment === "core" ? "non_core" : "core")
      : (exp.cost_behaviour === "fixed" ? "variable" : "fixed");
    try {
      await api.put(`/expenses/${exp.id}`, { [field]: next });
      setExpenses((prev) => prev.map((e) => (e.id === exp.id ? { ...e, [field]: next } : e)));
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const generateNext = async () => {
    if (!window.confirm(`Copy every ${year} expense into ${year + 1} with a 20% increase? The generated budget will be a Draft.`)) return;
    try {
      const { data } = await api.post(`/budget/generate-next?year=${year}`);
      toast.success(`Generated ${data.generated} expenses for ${year + 1} (Draft)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.put("/budget/status", { year, status });
      setMeta(data);
      toast.success(`Budget marked ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteExpense = async (exp) => {
    if (!window.confirm(`Delete "${exp.name}"?`)) return;
    await api.delete(`/expenses/${exp.id}`);
    toast.success("Deleted");
    load();
  };

  const downloadCsv = async (path, filename) => {
    const res = await fetch(`${API}${path}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/budget/import?year=${year}`, fd);
      toast.success(`Imported ${data.imported} rows${data.errors.length ? ` · ${data.errors.length} errors` : ""}`);
      if (data.errors.length) console.warn(data.errors);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
  };

  const grouped = useMemo(() => {
    const catMap = {};
    categories.forEach((c) => { catMap[c.id] = { ...c, expenses: [] }; });
    const orphans = [];
    expenses.forEach((e) => {
      if (catMap[e.category_id]) catMap[e.category_id].expenses.push(e);
      else orphans.push(e);
    });
    const groups = Object.values(catMap).filter((g) => g.expenses.length > 0).sort((a, b) => a.order - b.order);
    if (orphans.length) groups.push({ id: "_orphan", name: "Archived Category", expenses: orphans });
    return groups;
  }, [categories, expenses]);

  const monthlyTotals = useMemo(() => {
    const t = Array(12).fill(0);
    expenses.forEach((e) => e.months.forEach((m, i) => { t[i] += m; }));
    return t;
  }, [expenses]);
  const annual = monthlyTotals.reduce((a, b) => a + b, 0);
  const coreAnnual = useMemo(() => expenses.filter((e) => (e.reserve_treatment || "core") === "core")
    .reduce((a, e) => a + e.months.reduce((x, y) => x + y, 0), 0), [expenses]);
  const locked = meta?.status === "locked";

  const STATUS_BADGE = {
    draft: "bg-zinc-100 text-zinc-600 border-zinc-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    locked: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <div data-testid="budget-grid-container" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">Annual Budget {year}</h1>
            {meta && (
              <span data-testid="budget-status-badge" className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_BADGE[meta.status]}`}>
                {meta.status}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-500 mt-2">
            <span>Total budget: <span className="font-mono-num font-semibold text-zinc-900" data-testid="budget-total-annual">{formatINR(annual)}</span></span>
            <span>Core reserve requirement: <span className="font-mono-num font-semibold text-blue-600" data-testid="budget-core-annual">{formatINR(coreAnnual)}</span></span>
            <span>Non-core: <span className="font-mono-num font-semibold text-zinc-700" data-testid="budget-noncore-annual">{formatINR(annual - coreAnnual)}</span></span>
            <span>Monthly essential burn: <span className="font-mono-num font-semibold text-zinc-700" data-testid="budget-monthly-burn">{formatINR(coreAnnual / 12)}</span></span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {meta?.status === "draft" && (
              <Button data-testid="budget-approve-btn" size="sm" variant="outline" className="h-7 text-xs border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" onClick={() => setStatus("approved")}>
                Approve Budget
              </Button>
            )}
            {meta?.status === "approved" && (
              <>
                <Button data-testid="budget-lock-btn" size="sm" variant="outline" className="h-7 text-xs border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100" onClick={() => setStatus("locked")}>
                  Lock Budget
                </Button>
                <Button data-testid="budget-back-to-draft-btn" size="sm" variant="outline" className="h-7 text-xs border-[#E3E0D8] bg-white text-zinc-600" onClick={() => setStatus("draft")}>
                  Back to Draft
                </Button>
              </>
            )}
            {meta?.status === "locked" && (
              <Button data-testid="budget-unlock-btn" size="sm" variant="outline" className="h-7 text-xs border-[#E3E0D8] bg-white text-zinc-600" onClick={() => setStatus("approved")}>
                Unlock (Admin)
              </Button>
            )}
            <Button data-testid="budget-generate-next-btn" size="sm" variant="outline" className="h-7 text-xs border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100" onClick={generateNext}>
              <Sparkles size={12} className="mr-1" /> Generate {year + 1} Budget (+20%)
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="budget-csv-template-btn" variant="outline" size="sm" className="border-[#E3E0D8] bg-white text-zinc-700"
            onClick={() => downloadCsv("/budget/template", "budget_template.csv")}>
            <FileText size={14} className="mr-1.5" /> Template
          </Button>
          <Button data-testid="budget-csv-export-btn" variant="outline" size="sm" className="border-[#E3E0D8] bg-white text-zinc-700"
            onClick={() => downloadCsv(`/budget/export?year=${year}`, `budget_${year}.csv`)}>
            <Download size={14} className="mr-1.5" /> Export CSV
          </Button>
          <Button data-testid="budget-csv-import-btn" variant="outline" size="sm" className="border-[#E3E0D8] bg-white text-zinc-700"
            onClick={() => fileRef.current?.click()}>
            <Upload size={14} className="mr-1.5" /> Import CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="budget-csv-file-input"
            onChange={(e) => { if (e.target.files[0]) importCsv(e.target.files[0]); e.target.value = ""; }} />
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditExp(null); setForm({ category_id: "", name: "", notes: "", reserve_treatment: "core", cost_behaviour: "fixed" }); } }}>
            <DialogTrigger asChild>
              <Button data-testid="budget-add-expense-btn" size="sm" className="bg-blue-600 hover:bg-blue-500 text-white">
                <Plus size={14} className="mr-1.5" /> Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E3E0D8]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editExp ? `Edit "${editExp.name}"` : "New Expense"}</DialogTitle>
                <DialogDescription className="text-zinc-500 text-xs">{editExp ? "Change the name, category, notes or classification. Monthly values are edited in the grid." : "Define your own subcategory name under a main category."}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Main Category</label>
                  <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                    <SelectTrigger data-testid="expense-category-select" className="bg-[#F8F7F3] border-[#E3E0D8]">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Subcategory / Expense Name</label>
                  <Input data-testid="expense-name-input" placeholder="e.g. Delhi Staff Salaries, Founder Car"
                    className="bg-[#F8F7F3] border-[#E3E0D8]"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Reserve Treatment</label>
                    <Select value={form.reserve_treatment} onValueChange={(v) => setForm({ ...form, reserve_treatment: v })}>
                      <SelectTrigger data-testid="expense-treatment-select" className="bg-[#F8F7F3] border-[#E3E0D8]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="core">Essential / Core</SelectItem>
                        <SelectItem value="non_core">Non-Core</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Cost Behaviour</label>
                    <Select value={form.cost_behaviour} onValueChange={(v) => setForm({ ...form, cost_behaviour: v })}>
                      <SelectTrigger data-testid="expense-behaviour-select" className="bg-[#F8F7F3] border-[#E3E0D8]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed</SelectItem>
                        <SelectItem value="variable">Variable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Notes</label>
                  <Textarea data-testid="expense-notes-input" className="bg-[#F8F7F3] border-[#E3E0D8]" rows={2}
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <Button data-testid="expense-save-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white" onClick={addExpense}>
                  {editExp ? "Save Changes" : "Create Expense"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-surface overflow-hidden fade-up">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1180px]">
            <thead className="sticky top-0">
              <tr className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#E3E0D8] bg-[#FCFBF8]">
                <th className="px-4 py-3 text-left font-medium min-w-[200px]">Expense</th>
                {MONTHS.map((m) => <th key={m} className="px-1 py-3 text-right font-medium">{m}</th>)}
                <th className="px-3 py-3 text-right font-medium">Total</th>
                <th className="px-3 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 && (
                <tr><td colSpan={15} className="px-6 py-12 text-center text-zinc-500">
                  No expenses for {year} yet. Add your first expense or import a CSV.
                </td></tr>
              )}
              {grouped.map((g) => (
                <BudgetGroup key={g.id} group={g} setCell={setCell} dirty={dirty} saveRow={saveRow} deleteExpense={deleteExpense} toggleClass={toggleClass} locked={locked} onEdit={openEdit} />
              ))}
            </tbody>
            {expenses.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-blue-500/40 bg-[#FCFBF8]">
                  <td className="px-4 py-3.5 font-heading font-semibold text-zinc-900">Monthly Totals</td>
                  {monthlyTotals.map((t, i) => (
                    <td key={i} className="px-1 py-3.5 text-right font-mono-num text-xs font-bold text-blue-600" data-testid={`budget-month-total-${i}`}>
                      {formatINR(t)}
                    </td>
                  ))}
                  <td className="px-3 py-3.5 text-right font-mono-num text-xs font-extrabold text-zinc-900" data-testid="budget-annual-total">
                    {formatINR(annual)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <p className="text-xs text-zinc-400">Enter plain rupee amounts (e.g. 250000). Totals display in Indian format (₹2.50 L). Click Save on a row after editing.</p>
    </div>
  );
}

const BudgetGroup = ({ group, setCell, dirty, saveRow, deleteExpense, toggleClass, locked, onEdit }) => (
  <>
    <tr className="bg-[#F4F2EC]">
      <td colSpan={15} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-blue-600">{group.name}</td>
    </tr>
    {group.expenses.map((e) => (
      <tr key={e.id} className="border-b border-[#ECE9E1] hover:bg-[#FAF9F4] transition-colors group">
        <td className="px-4 py-2">
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-zinc-800 text-[13px]">{e.name}</p>
            {!locked && (
              <>
                <button data-testid={`expense-edit-${e.id}`} onClick={() => onEdit(e)}
                  className="p-1 rounded text-zinc-400 hover:text-blue-600 hover:bg-blue-50" title="Edit name, category & notes">
                  <Pencil size={12} />
                </button>
                <button data-testid={`expense-delete-${e.id}`} onClick={() => deleteExpense(e)}
                  className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50" title="Delete expense">
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
          <div className="flex gap-1 mt-0.5">
            <button data-testid={`expense-treatment-toggle-${e.id}`} onClick={() => toggleClass(e, "reserve_treatment")} disabled={locked}
              className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-px border transition-colors ${
                (e.reserve_treatment || "core") === "core" ? "text-blue-700 border-blue-200 bg-blue-50" : "text-zinc-500 border-zinc-200 bg-zinc-50"
              }`} title="Click to toggle Core / Non-Core">
              {(e.reserve_treatment || "core") === "core" ? "Core" : "Non-Core"}
            </button>
            <button data-testid={`expense-behaviour-toggle-${e.id}`} onClick={() => toggleClass(e, "cost_behaviour")} disabled={locked}
              className="text-[9px] uppercase tracking-wider rounded-full px-1.5 py-px border text-zinc-500 border-zinc-200 bg-white transition-colors"
              title="Click to toggle Fixed / Variable">
              {(e.cost_behaviour || "fixed") === "fixed" ? "Fixed" : "Variable"}
            </button>
          </div>
          {e.notes && <p className="text-[11px] text-zinc-400 truncate max-w-[190px]">{e.notes}</p>}
        </td>
        {e.months.map((v, i) => (
          <td key={i} className="px-0.5 py-1.5">
            <input
              data-testid="budget-cell-input"
              type="number"
              min="0"
              className="w-[74px] bg-transparent border border-transparent hover:border-[#E3E0D8] focus:border-blue-500 focus:bg-[#F8F7F3] rounded px-1.5 py-1 text-right font-mono-num text-xs text-zinc-800 outline-none transition-colors"
              value={v === 0 ? "" : v}
              placeholder="0"
              disabled={locked}
              onChange={(ev) => setCell(e.id, i, ev.target.value)}
            />
          </td>
        ))}
        <td className="px-3 py-2 text-right font-mono-num text-xs font-semibold text-zinc-900">
          {formatINR(e.months.reduce((a, b) => a + b, 0))}
        </td>
        <td className="px-3 py-2">
          {!locked && (
          <div className="flex items-center gap-1 justify-end">
            {dirty[e.id] && (
              <button data-testid={`budget-row-save-${e.id}`} onClick={() => saveRow(e)}
                className="p-1.5 rounded text-blue-600 hover:bg-blue-600/10" title="Save row">
                <Save size={14} />
              </button>
            )}
            <button data-testid={`budget-row-delete-${e.id}`} onClick={() => deleteExpense(e)}
              className="p-1.5 rounded text-zinc-500 hover:text-red-600 hover:bg-red-500/10 transition-colors" title="Delete">
              <Trash2 size={13} />
            </button>
          </div>
          )}
        </td>
      </tr>
    ))}
  </>
);
